import { Checkbox, Form, message, Select, Input } from 'antd';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionButton from '../../components/action-button';
import InputName from '../../components/input-name';
import MiniFormDrawer from '../../components/mini-form-drawer';
import TextItem from '../../components/text-item';
import useFormatter from '../../hooks/useFormatter';
import unwrapAxiosPromise from '../../util/unwrapAxiosPromise';
import InputAnexo from "../../components/input-anexo";
import { valuesToFormData } from "../../util/formData";
import InputNumber from "../../components/input-number";
import styles from "./_styles.less";
import { Modulos } from "../../constants";

const defaultValue = {
  nome: '',
  logo: null,
  modulos: [],
};

export default function AmbienteDrawer({ loading, onAfterClose, onChange, onClose, value = defaultValue, visible }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatter();
  const { formatBytes } = useFormatter();
  const [form] = Form.useForm();
  const [loadingSave, setLoadingSave] = useState(false);

  useEffect(() => {
  form.resetFields();
  form.setFieldsValue(value);
}, [value]);


  const fireChange = () => typeof onChange === 'function' ? onChange() : undefined;
  const fireClose = () => typeof onClose === 'function' ? onClose() : undefined;

  const modulosOptions = Modulos.map(tab => ({ label: t(tab.label), value: tab.id }))

 function bytesToReadable(bytes) {
  if (!bytes || isNaN(bytes)) return { number: 0, unit: 'MB' };

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const number = parseFloat((bytes / Math.pow(1024, i)).toFixed(2));

  return { number, unit: units[i] };
}


const armazenamentoAtual = bytesToReadable(Number(value?.armazenamentoAtual || 0));
useEffect(() => {
  if (!value) return;

  const mappedValue = {
    ...value,
    limiteArmazenamento: bytesToReadable(value.limiteArmazenamento || 0)
  };

  form.setFieldsValue(mappedValue);
}, [value]);


  const handleDrawerAfterOpen = () => {
    document.querySelector('#nome').focus();
  };

 const handleFinish = values => {
  const limite = values.limiteArmazenamento || { number: 0, unit: 'MB' };
  const number = Number(limite.number) || 0; 
  const unit = limite.unit || 'MB';
  let bytes = number;

  switch (unit) {
    case 'MB':
      bytes = number * 1024 * 1024;
      break;
    case 'GB':
      bytes = number * 1024 * 1024 * 1024;
      break;
    case 'TB':
      bytes = number * 1024 * 1024 * 1024 * 1024;
      break;
  }

  values.limiteArmazenamento = bytes;

  setLoadingSave(true);
  const formData = valuesToFormData(values);
  const promise = value.id ?
    axios.put(`/ambientes/${value.id}`, formData) :
    axios.post('/ambientes', formData);

  unwrapAxiosPromise(promise, form)
    .then(() => {
      message.success(t(value.id ?
        'Ambiente alterado com sucesso' :
        'Ambiente incluído com sucesso'));
      fireChange();
      fireClose();
    })
    .finally(() => setLoadingSave(false));
};


  const footer = (
    <>
      <ActionButton.Save
        onClick={() => form.submit()}
        loading={loadingSave}
      />
      <ActionButton.Close
        onClick={fireClose}
        disabled={loadingSave}
      />
    </>
  );

  const drawerTitle = value.id ? (value.id + ' - ' + value.nome) : t('Criar Ambiente');

  return (
    <MiniFormDrawer
      drawerTitle={drawerTitle}
      drawerFooter={footer}
      drawerWidth={440}
      drawerVisible={visible}
      form={form}
      loading={loading}
      onDrawerClose={fireClose}
      onDrawerAfterOpen={handleDrawerAfterOpen}
      onDrawerAfterClose={onAfterClose}
      onFinish={handleFinish}
    >
      <Form.Item name="nome" label={t('Nome')}>
        <InputName disabled={loadingSave}/>
      </Form.Item>
      <Form.Item name="logo" label={t('Logo')}>
        <InputAnexo accept="image/*" disabled={loadingSave}/>
      </Form.Item>
      <Form.Item name="numeroUsuarios" label={t('Número de Usuários (Nível de Acesso de 1 até 5)')}>
        <InputNumber disabled={loadingSave} maximumFractionDigits={0}/>
      </Form.Item>
      <Form.Item name="numeroEmpresas" label={t('Número de Empresas')}>
        <InputNumber disabled={loadingSave} maximumFractionDigits={0}/>
      </Form.Item>
<Form.Item
  name="limiteArmazenamento"
  label={t('Limite de armazenamento')}
  initialValue={{ number: 0, unit: 'MB' }}
>
  <Input.Group compact>
    <Form.Item
      noStyle
      shouldUpdate={(prev, curr) =>
        prev.limiteArmazenamento?.number !== curr.limiteArmazenamento?.number ||
        prev.limiteArmazenamento?.unit !== curr.limiteArmazenamento?.unit
      }
    >
      {({ getFieldValue, setFieldsValue }) => {
        const current = getFieldValue('limiteArmazenamento') || { number: 0, unit: 'MB' };

        const handleNumberChange = (num) => {
          setFieldsValue({
            limiteArmazenamento: { ...current, number: num },
          });
        };

        const handleUnitChange = (unit) => {
          setFieldsValue({
            limiteArmazenamento: { ...current, unit },
          });
        };

        return (
          <>
            <InputNumber
              min={0}
              style={{ width: '70%' }}
              disabled={loadingSave}
              value={current.number}
              onChange={handleNumberChange}
            />
            <Select
              style={{ width: '30%' }}
              value={current.unit}
              onChange={handleUnitChange}
            >
              <Select.Option value="MB">MB</Select.Option>
              <Select.Option value="GB">GB</Select.Option>
              <Select.Option value="TB">TB</Select.Option>
            </Select>
          </>
        );
      }}
    </Form.Item>
  </Input.Group>
</Form.Item>
<TextItem
  label={`Armazenamento Atual: ${formatBytes(Number(value?.armazenamentoAtual || 0))}`}
/>
      <Form.Item name="limiteAssinaturas" label={t('Limite de Assinaturas')}>
        <InputNumber disabled={loadingSave} maximumFractionDigits={0}/>
      </Form.Item>
      <Form.Item name="modulos" label={t('Módulos')}>
        <Checkbox.Group className={styles['checkbox-vertical']} disabled={loadingSave} options={modulosOptions}/>
      </Form.Item>
      <TextItem label="Criado em">{formatDateTime(value?.createdAt)}</TextItem>
      <TextItem label="Alterado em">{formatDateTime(value?.updatedAt)}</TextItem>
    </MiniFormDrawer>
  );
}

AmbienteDrawer.propTypes = {
  loading: PropTypes.bool,
  onAfterClose: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  value: PropTypes.object,
  visible: PropTypes.bool,
};
